import { useRef } from 'react';

import { DIRECTION_X, DIRECTION_Y } from './constants';
import { useGesture } from 'react-use-gesture';
import { useClampedSpring } from './use-clamped-spring';
import { useFunction } from './use-function';

export const useAnimatedTrack = (
  direction,
  actions,
  dragTreshold = 300,
  inTransitionRef,
  allowNext = true,
  allowPrev = true,
) => {
  // do all the animation here
  const [props, set, stop] = useClampedSpring(0);
  const internalPositionRef = useRef(null);
  const transitionToPosition = useFunction(
    (position, realPosition = null, immediate) => {
      if (!isNaN(position) && position !== internalPositionRef.current) {
        stop();
        actions.transitionStart();
        internalPositionRef.current = position;
        set({
          value: position,
          immediate,
          onStop: () => {
            if (typeof realPosition === 'number' && position !== realPosition) {
              internalPositionRef.current = realPosition;
              set({
                value: realPosition,
                immediate: true,
                onStop: () => {
                  actions.transitionEnd();
                },
              });
            } else {
              actions.transitionEnd();
            }
          },
        });
      }
    },
  );
  const isDragging = useRef(false);
  const bind = useGesture({
    onHover: ({ hovering }) =>
      hovering ? actions.hoverStart() : actions.hoverEnd(),
    onDrag: ({
      movement, // * last gesture offset (xy - initial)
      distance, // offset distance
      // first, // * true when it's the first event
      last, // * true when it's the last event
      cancel,
    }) => {
      const movementObj = {
        [DIRECTION_X]: movement[0],
        [DIRECTION_Y]: movement[1],
      };
      const first = !isDragging.current && distance > 2;

      if (first) {
        if (inTransitionRef.current) {
          // never start drag when transitioning
          cancel();
        } else {
          isDragging.current = true;
          actions.transitionStart();
        }
        // Prevent scroll on touch screens while dragging:
        document.ontouchmove = function(e) {
          e.preventDefault();
        };
      } else if (last) {
        isDragging.current = false;
        document.ontouchmove = function() {
          return true;
        };
        let action = movementObj[direction] < 0 ? actions.next : actions.prev;
        if (
          distance < dragTreshold ||
          (action === actions.next && !allowNext) ||
          (action === actions.prev && !allowPrev)
        ) {
          set({
            value: internalPositionRef.current,
            onStop: actions.transitionEnd,
          });
        } else {
          actions.transitionEnd();
          action();
        }
      } else if (isDragging.current) {
        set({
          value: internalPositionRef.current + movementObj[direction],
          immediate: true,
        });
      }
    },
  });
  return [props.value, bind, transitionToPosition];
};
